import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { createEditor, Editor, Transforms } from 'slate'
import {
  Slate,
  withReact,
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  RenderPlaceholderProps
} from 'slate-react'
import { withHistory } from 'slate-history'

import { SxProps } from '@mui/material'
import { UserAgent } from 'types/GlobalAccount'

import { INPUT_BACKGROUND } from 'themes'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text, { TextVariantType } from 'components_new/atoms/Text'

import Element from './renders/Element'
import Leaf from './renders/Leaf'
import Placeholder from './renders/Placeholder'
import Toolbar from './Toolbar'

import { RichText, TRichTextSettings } from './types'
import { handleKeyDown, isEditorEmpty } from './utils'
import { EMPTY_VALUE } from './lib'

interface Props {
  actions?: ReactNode
  autoFocus?: boolean
  color?: string
  contentMaxWidth?: string | number
  edit: boolean
  editHeight?: number
  error?: boolean
  externalLabel?: string
  helperText?: string
  maxLines?: number
  noGutter?: boolean
  onBlur?: (value: RichText | null) => void
  onChange?: (value: RichText | null) => void
  onCancel?: () => void
  onEdit?: () => void
  onSave?: (value: RichText | null) => void
  paragraphVariant?: TextVariantType
  placeholder?: string
  readOnly?: boolean
  settings?: TRichTextSettings
  sx?: SxProps
  value: RichText | null
  variant?: 'contained' | 'outlined'
}

const RichTextEditor = React.forwardRef((props: Props, ref: any) => {
  const {
    actions,
    autoFocus,
    color,
    contentMaxWidth,
    edit,
    editHeight,
    error,
    externalLabel,
    helperText,
    maxLines,
    noGutter,
    onBlur,
    onChange,
    onCancel,
    onEdit,
    onSave,
    paragraphVariant,
    placeholder,
    settings,
    sx,
    value,
    variant
  } = props

  const { t } = useTranslation()

  const [editor] = useState(() => withReact(withHistory(createEditor())))
  const currentValue = useRef<RichText | null>(value)

  useEffect(() => {
    if (autoFocus && edit) {
      ReactEditor.focus(editor)
      Transforms.select(editor, Editor.end(editor, []))
    }
  }, [autoFocus, edit])

  // Slate only consumes `initialValue` on mount, so external `value` changes
  // won't reflect in the editor unless we imperatively replace its content.
  // We compare against `currentValue` (updated on each keystroke) to avoid
  // resetting the editor on every round-trip through the parent's state.
  useEffect(() => {
    if (JSON.stringify(value) === JSON.stringify(currentValue.current)) return

    const newValue = value ?? EMPTY_VALUE

    editor.children = newValue
    editor.history = { redos: [], undos: [] }
    Transforms.deselect(editor)
    editor.onChange()
    currentValue.current = value
  }, [value])

  const handleSave = () => {
    if (onSave) onSave(currentValue.current)
  }

  const handleBlur = () => {
    if (onBlur) onBlur(currentValue.current)
  }

  const handleCancel = () => {
    const resetTo = value ?? EMPTY_VALUE

    editor.children = resetTo
    editor.history = { redos: [], undos: [] }
    Transforms.deselect(editor)
    editor.onChange()
    currentValue.current = value

    if (onCancel) onCancel()
  }

  const userAgent: UserAgent = useMemo(() => {
    if (
      typeof navigator !== 'undefined' &&
      navigator.userAgent.includes('Mac')
    ) {
      return 'mac'
    } else {
      return 'windows'
    }
  }, [])

  const renderElement = useCallback(
    ({ attributes, children, element }: RenderElementProps) => (
      <Element
        attributes={attributes}
        element={element}
        paragraphVariant={paragraphVariant}
      >
        {children}
      </Element>
    ),
    [paragraphVariant]
  )

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return <Leaf {...props} />
  }, [])

  const renderPlaceholder = (props: RenderPlaceholderProps) => (
    <Placeholder {...props} />
  )

  return (
    <Box
      sx={{
        ...(noGutter ? null : { mb: 1 }),
        ...sx
      }}
      ref={ref}
    >
      {externalLabel ? (
        <Text
          color="text.primary"
          fontWeight="medium"
          variant="body2"
          sx={{ mb: 0.25 }}
        >
          {props.externalLabel}
        </Text>
      ) : null}
      <Slate
        editor={editor}
        initialValue={value ?? EMPTY_VALUE}
        onChange={(slateValue: RichText) => {
          const isAstChange = editor.operations.some(
            (op) => 'set_selection' !== op.type
          )

          if (isAstChange) {
            const nextValue = isEditorEmpty(slateValue) ? null : slateValue

            currentValue.current = nextValue
            if (onChange) onChange(nextValue)
          }
        }}
      >
        <Box
          sx={{
            backgroundColor:
              edit && variant === 'contained' ? INPUT_BACKGROUND : undefined,
            border: edit && variant === 'outlined' ? '1px solid' : undefined,
            borderRadius: edit ? '8px' : undefined,
            borderColor: 'divider'
          }}
        >
          {edit ? (
            <Toolbar
              actions={actions}
              settings={settings}
              sx={{
                mb: 0.25,
                p: 1,
                borderBottom: '1px solid',
                borderBottomColor: 'divider'
              }}
              userAgent={userAgent}
            />
          ) : null}
          <Box
            sx={{
              p: edit ? '12px' : undefined,
              maxWidth: contentMaxWidth,
              position: 'relative'
            }}
          >
            <Box
              onClick={!edit && onEdit ? onEdit : undefined}
              sx={{
                color,
                cursor: !edit && onEdit ? 'text' : undefined,
                ...(!edit && maxLines
                  ? {
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: maxLines
                    }
                  : { overflowY: 'hidden' }),
                position: 'relative',
                ['&:hover']: {
                  bgcolor: !edit && onEdit ? 'action.hover' : undefined
                }
              }}
            >
              <Editable
                autoFocus={autoFocus}
                onBlur={handleBlur}
                onKeyDown={(event) => handleKeyDown(editor, event, userAgent)}
                placeholder={placeholder}
                readOnly={!edit}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                renderPlaceholder={renderPlaceholder}
                spellCheck={true}
                style={{
                  minHeight: edit ? `${editHeight ?? 196}px` : undefined,
                  outline: 'none'
                }}
              />
            </Box>
          </Box>
        </Box>
        {helperText && edit ? (
          <Text
            color={error ? 'error' : 'text.secondary'}
            component="p"
            sx={{ pt: '3px', mx: '14px', mb: '0px' }}
            variant="caption"
          >
            {helperText}
          </Text>
        ) : null}
        {edit && onSave && onCancel ? (
          <Box
            sx={{
              display: 'flex',
              gap: 0.25,
              justifyContent: 'flex-end',
              mt: 0.5
            }}
          >
            <Button onClick={handleCancel} size="small" variant="text">
              {t('global', 'cancel')}
            </Button>

            <Button onClick={handleSave} size="small">
              {t('global', 'save')}
            </Button>
          </Box>
        ) : null}
      </Slate>
    </Box>
  )
})

export default RichTextEditor
